/* eslint-disable import/prefer-default-export */
import { PropertyWithOccupants } from '@centecomm/portal-service';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingContent, ToggleIcon } from '../../PageElements';
import { Search } from '../../Search';
import { PropertySelection } from '../PropertySelection';
import { PropertyTenantSelectionHeader } from '../PropertyTenantSelectionHeader';
import styles from '../property-tenant-resolution.module.css';

interface DropDownSelectorProps {
    availableOptions: any[];
    selectedOptionIds?: number[];
    visibleOptions: number[];
    searchHandler?: (searchTerm: string) => void;
    selectOptionHandler: (propertyIds: number[]) => void;
    loaded: boolean;
    searchPlaceholder?: string;
    loadingMessage?: string;
    headerType?: 'Tenant' | 'Property' | 'Company' | 'T' | 'P' | 't' | 'p' | 'Space';
    listDisabled?: boolean;
    multiple?: boolean;
    hideSearch?: boolean;
    hideToggleIcon?: boolean;
    placeholderOverride?: string;
    listContainerStyle?: React.CSSProperties;
}

export const DropDownSelector: React.FC<DropDownSelectorProps> = ({
    availableOptions,
    selectedOptionIds = [],
    visibleOptions,
    searchHandler,
    selectOptionHandler,
    loaded,
    searchPlaceholder,
    loadingMessage,
    headerType = 'Property',
    listDisabled = false,
    multiple = false,
    hideSearch = false,
    hideToggleIcon = false,
    placeholderOverride,
    listContainerStyle = {},
}) => {
    const { t } = useTranslation(['forms']);
    const [filterVisible, toggleFilter] = React.useState<boolean>(false);
    const [selectedProperties, setSelectedProperties] = React.useState<PropertyWithOccupants[]>([]);
    const [showList, toggleShowList] = React.useState<boolean>(false);
    const [showSelected, toggleShowSelected] = React.useState<boolean>(false);
    const [propertyList, setPropertyList] = React.useState<PropertyWithOccupants[]>(availableOptions);

    const visibleOptionsSet = new Set(visibleOptions);

    React.useEffect(() => {
        if (showSelected) {
            setPropertyList(availableOptions.filter((property) => selectedOptionIds?.includes(property.id)));
        } else {
            setPropertyList(availableOptions);
        }
    }, [showSelected, availableOptions.length]);

    React.useEffect(() => {
        const selectedObjects = availableOptions.filter((p: PropertyWithOccupants) => selectedOptionIds?.includes(p.id));
        if (selectedObjects.length && selectedOptionIds) selectOptionHandler?.(selectedOptionIds);
        setSelectedProperties(selectedObjects);
    }, [selectedOptionIds, loaded]);

    const action = () => {
        toggleShowList(!showList);
        searchHandler?.('');
        toggleFilter(false);
    };

    const handlePropertySelection = (id: number) => {
        let updatedSelection: number[] = [];

        if (selectedOptionIds.includes(id)) {
            updatedSelection = selectedOptionIds.filter((propertyId) => propertyId !== id);
        } else {
            updatedSelection = multiple ? [...selectedOptionIds, id] : [id];
        }

        if (!multiple && updatedSelection.length > 0) {
            toggleShowList(false);
        }

        selectOptionHandler(updatedSelection);
    };

    const clearSearch = () => {
        toggleFilter(false);
        searchHandler?.('');
    };

    const handleSearch = (searchTerm: string): void => {
        if (!filterVisible) toggleFilter(true);

        searchHandler?.(searchTerm);
    };

    return loaded ? (
        <>
            <PropertyTenantSelectionHeader
                propertyOrOccupants={selectedProperties}
                handler={action}
                listActive={showList}
                type={headerType}
                withMargin
                listDisabled={listDisabled}
                placeholderOverride={placeholderOverride}
            />
            {showList && !listDisabled && (
                <>
                    <Search
                        hide={hideSearch}
                        handler={handleSearch}
                        placeholder={searchPlaceholder || t('forms:selectors.properties.search')}
                        cleanUpCallback={clearSearch}
                        clearCallback={clearSearch}
                    />
                    <ToggleIcon
                        hide={hideToggleIcon}
                        active={showSelected}
                        message={t('forms:selectors.showSelectedOnly')}
                        toggle={() => toggleShowSelected(!showSelected)}
                    />
                    <ul className={`${styles.list} ${styles.PropertySelect}`} style={listContainerStyle}>
                        {propertyList
                            .filter((p: PropertyWithOccupants) => !filterVisible || visibleOptionsSet.has(p.id))
                            .map((property: PropertyWithOccupants) => (
                                <PropertySelection
                                    key={`${property.id}-available`}
                                    property={property}
                                    togglePropertySelection={handlePropertySelection}
                                    activePropertyIds={selectedOptionIds}
                                />
                            ))}
                    </ul>
                </>
            )}
        </>
    ) : (
        <LoadingContent message={loadingMessage || t('common:loading')} />
    );
};

export default DropDownSelector;
